"use client";

import React, { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  ApplicationsStatuses,
  updateApplicationInterviewDate,
  updateApplicationInterviewLink
} from "./utils";
import { ApplicationStatusManagement } from "./ApplicationStatusManagement";
import { ConfirmationDialog } from "@/components/common/ConfirmationDialog";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverTrigger,
  PopoverContent
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import {
  Calendar as CalendarIcon,
  Loader2,
  Check,
  Edit2,
  Eye
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { uploadMockInterview, deleteMockInterview } from "@/lib/supabase";
import { Loading } from "@/components/ui/loading";

const TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hours = Math.floor(index / 2);
  const minutes = index % 2 === 0 ? "00" : "30";
  return `${String(hours).padStart(2, "0")}:${minutes}`;
});

const BERLIN_TIMEZONE = "Europe/Berlin";

const getBerlinDateParts = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: BERLIN_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  const [year, month, day] = formatter.format(date).split("-");

  return {
    year,
    month,
    day
  };
};

const getBerlinOffset = (
  year: number,
  month: number,
  day: number,
  hours: number,
  minutes: number
) => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: BERLIN_TIMEZONE,
    timeZoneName: "short"
  });

  const utcReferenceDate = new Date(
    Date.UTC(year, month - 1, day, hours, minutes)
  );
  const tzName =
    formatter
      .formatToParts(utcReferenceDate)
      .find((part) => part.type === "timeZoneName")?.value || "GMT+1";

  const match = tzName.match(/GMT([+-]\d{1,2})/);
  const offsetHours = match ? parseInt(match[1], 10) : 1;
  const sign = offsetHours >= 0 ? "+" : "-";
  const paddedHours = String(Math.abs(offsetHours)).padStart(2, "0");

  return `${sign}${paddedHours}:00`;
};

const buildBerlinIsoString = (date: Date, time: string) => {
  const { year, month, day } = getBerlinDateParts(date);
  const [hoursStr, minutesStr] = time.split(":");
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);
  const offset = getBerlinOffset(
    Number(year),
    Number(month),
    Number(day),
    hours,
    minutes
  );

  return `${year}-${month}-${day}T${hoursStr}:${minutesStr}:00${offset}`;
};

export interface ProgressSectionProps {
  title: string;
  sectionKey: string;
  isCompleted: boolean;
  isEditing: boolean;
  onToggleEdit: () => void;
  children: React.ReactNode;
  viewComponent?: React.ReactNode;
  isLast?: boolean;
  isNextSectionCompleted?: boolean;
}

export const ProgressSection: React.FC<ProgressSectionProps> = ({
  title,
  isCompleted,
  isEditing,
  onToggleEdit,
  children,
  viewComponent,
  isLast = false,
  isNextSectionCompleted = false
}) => {
  const { t } = useTranslation(["applications", "common"]);
  return (
    <div className="relative">
      {/* Vertical Line */}
      {!isLast && (
        <div className="absolute left-4 top-8 w-0.5 h-full bg-gray-200 z-0" />
      )}
      {!isLast && isCompleted && isNextSectionCompleted && (
        <div className="absolute left-4 top-8 w-0.5 h-full bg-green-600 z-0" />
      )}

      <div className="flex items-start gap-4 pb-6">
        {/* Progress Circle with Hover Edit */}
        <div className="relative group">
          <div
            className={cn(
              "relative z-10 flex items-center justify-center w-8 h-8 rounded-full transition-all flex-shrink-0 cursor-pointer",
              isCompleted
                ? "bg-green-600 hover:bg-green-700"
                : "bg-gray-200 hover:bg-gray-300"
            )}
            onClick={onToggleEdit}
          >
            {/* Icon logic: checkmark -> pen on hover, pen -> eye on hover */}
            {isCompleted && !isEditing && (
              <>
                <Check className="w-4 h-4 text-white group-hover:hidden" />
                <Edit2 className="w-4 h-4 text-white hidden group-hover:block" />
              </>
            )}
            {isEditing && (
              <>
                <Edit2 className="w-4 h-4 text-white group-hover:hidden" />
                <Eye className="w-4 h-4 text-white hidden group-hover:block" />
              </>
            )}
            {!isCompleted && !isEditing && (
              <Edit2 className="w-4 h-4 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
          </div>

          {/* Tooltip - positioned to the right */}
          <div className="absolute top-1/2 left-10 -translate-y-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20">
            {isEditing ? t("messages.switch_to_view") : t("messages.edit")}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="mb-3">
            <h3 className="font-medium text-lg">{title}</h3>
          </div>

          {/* Remove the wrapper border since sections have their own borders */}
          <div>
            {isEditing
              ? children
              : viewComponent || (
                  <div className="text-gray-500 italic bg-white border rounded-lg p-4">
                    {t("messages.no_data_available")}
                  </div>
                )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface LeftSidebarProps {
  application: any;
  statusUpdating?: boolean;
  handleStatusChange?: (status: ApplicationsStatuses) => void;
  onTabChange: (tab: string) => void;
  showJobTab?: boolean;
  showActionsTab?: boolean;
  showStatusManagement?: boolean;
  canEditInterviewDate?: boolean;
  canUploadMockInterview?: boolean;
  canViewCalendlyLink?: boolean;
  canViewNotesTab?: boolean;
  canViewTasksTab?: boolean;
}

export function LeftSidebar({
  application,
  statusUpdating = false,
  handleStatusChange,
  onTabChange,
  showJobTab = false,
  showActionsTab = false,
  showStatusManagement = false,
  canEditInterviewDate = false,
  canUploadMockInterview = false,
  canViewCalendlyLink = false,
  canViewNotesTab = false,
  canViewTasksTab = false
}: LeftSidebarProps) {
  const { t } = useTranslation(["applications", "common"]);
  const { toast } = useToast();
  // We use "general" as default if it's talent profile, and "job" if it shows the job tab
  const [activeTab, setActiveTab] = useState(showJobTab ? "job" : "general");
  const [selectedInterviewDate, setSelectedInterviewDate] =
    useState<Date | null>(null);
  const [selectedInterviewTime, setSelectedInterviewTime] =
    useState<string>("");
  const [isSavingInterviewDate, setIsSavingInterviewDate] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [interviewLinkValue, setInterviewLinkValue] = useState("");
  const [isUpdatingInterviewLink, setIsUpdatingInterviewLink] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [isDeletingVideo, setIsDeletingVideo] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!application?.interview_date) {
      setSelectedInterviewDate(null);
      setSelectedInterviewTime("");
      return;
    }

    const interviewDate = new Date(application.interview_date);
    const { year, month, day } = getBerlinDateParts(interviewDate);
    const berlinDate = new Date(Number(year), Number(month) - 1, Number(day));

    const berlinTimeFormatter = new Intl.DateTimeFormat("de-DE", {
      timeZone: BERLIN_TIMEZONE,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });

    setSelectedInterviewDate(berlinDate);
    setSelectedInterviewTime(berlinTimeFormatter.format(interviewDate));
  }, [application?.interview_date]);

  const formattedInterviewDate = useMemo(() => {
    if (!selectedInterviewDate) return "";
    return format(selectedInterviewDate, "PPP");
  }, [selectedInterviewDate]);

  const handleSaveInterviewDate = async () => {
    if (!selectedInterviewDate || !selectedInterviewTime) {
      toast({
        title: "Missing information",
        description: "Please select both a date and a time.",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSavingInterviewDate(true);

      const isoString = buildBerlinIsoString(
        selectedInterviewDate,
        selectedInterviewTime
      );
      await updateApplicationInterviewDate(
        application.application_id,
        isoString
      );
      if (application) {
        application.interview_date = isoString;
      }
      toast({
        title: "Success",
        description: "Interview date saved successfully."
      });
    } catch (error) {
      console.error("Error saving interview date:", error);
      toast({
        title: "Error",
        description: "Unable to save the interview date. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSavingInterviewDate(false);
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    onTabChange(tab);
  };

  const handleInterviewLinkUpdate = async () => {
    if (!application?.application_id) {
      toast({
        title: "Missing application",
        description: "Unable to find the application id for this record.",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsUpdatingInterviewLink(true);
      await updateApplicationInterviewLink(
        application.application_id,
        interviewLinkValue || null
      );
      application.interview_link = interviewLinkValue || null;
      toast({
        title: "Interview link updated",
        description: "The interview link was saved successfully."
      });
    } catch (error) {
      console.error("Error updating interview link:", error);
      toast({
        title: "Error",
        description: "Unable to save the interview link. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsUpdatingInterviewLink(false);
    }
  };

  const handleUploadVideo = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !application?.talent_id) return;

    try {
      setIsUploadingVideo(true);
      const result = await uploadMockInterview(file, application.talent_id);

      if (!result.success) {
        if (result.error === "FILE_TOO_LARGE") {
          toast({
            title: "Error",
            description: "File is too large. Maximum size is 200MB.",
            variant: "destructive"
          });
        } else if (result.error === "INVALID_TYPE") {
          toast({
            title: "Error",
            description: "Invalid file type. Please upload a valid video file.",
            variant: "destructive"
          });
        } else {
          throw new Error("Upload failed");
        }
        return;
      }

      toast({
        title: "Success",
        description: "Interview video uploaded successfully."
      });

      // Update the application object locally with the new video URL path if available
      // Note: Full reload or state lifting might be needed for the avatar to update immediately
      if (application) {
        application.mock_interview_video = result.path;
      }
    } catch (error) {
      console.error("Error uploading video:", error);
      toast({
        title: "Error",
        description: "Failed to upload video. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsUploadingVideo(false);
      // Reset input so the same file can be selected again if needed
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeleteVideo = async () => {
    if (!application?.talent_id || !application?.mock_interview_video) return;

    try {
      setIsDeletingVideo(true);
      const result = await deleteMockInterview(
        application.talent_id,
        application.mock_interview_video
      );

      if (!result.success) {
        toast({
          title: "Error",
          description: "Failed to delete video. Please try again.",
          variant: "destructive"
        });
        return;
      }

      toast({
        title: "Success",
        description: "Interview video deleted successfully."
      });

      if (application) {
        application.mock_interview_video = null;
      }
    } catch (error) {
      console.error("Error deleting video:", error);
      toast({
        title: "Error",
        description: "Failed to delete video. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeletingVideo(false);
    }
  };

  useEffect(() => {
    setInterviewLinkValue(application?.interview_link || "");
  }, [application?.interview_link]);

  if (isUploadingVideo) {
    return (
      <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80">
        <Loading text="Uploading video..." />
      </div>
    );
  }

  const TabButton = ({
    title,
    id,
    className = "w-full text-left"
  }: {
    title: string;
    id: string;
    className?: string;
  }) => (
    <button
      className={`${className} px-3 py-2 rounded font-medium ${activeTab === id ? "bg-gray-100" : "text-gray-600 hover:bg-gray-50"}`}
      onClick={() => handleTabChange(id)}
    >
      {title}
    </button>
  );

  return (
    <div className="w-full md:w-64 border-b md:border-b-0 md:border-r md:overflow-y-scroll h-auto md:h-full flex-shrink-0">
      <nav className="flex space-x-2 md:block md:space-y-1 md:space-x-0 overflow-x-auto md:pb-0 no-scrollbar">
        {showJobTab && (
          <TabButton
            title={t("navigation.applied_job")}
            id="job"
            className="whitespace-nowrap flex-shrink-0 md:w-full md:text-left"
          />
        )}
        <TabButton title={t("navigation.general")} id="general" />
        <TabButton title={t("navigation.experience")} id="experience" />
        <TabButton title={t("navigation.education")} id="education" />
        <TabButton title={t("navigation.documents")} id="documents" />
        {canViewNotesTab && (
          <TabButton
            title={t("common:navigation.notes_and_comments")}
            id="notes"
          />
        )}
        {canViewTasksTab && (
          <TabButton title={t("common:tasks.tab_title")} id="tasks" />
        )}
        {showActionsTab && (
          <TabButton
            title={t("common:buttons.actions")}
            id="actions"
            className="whitespace-nowrap flex-shrink-0 md:hidden"
          />
        )}
      </nav>

      <div
        className={cn(
          "px-4",
          activeTab === "actions" ? "block mt-4 p-4" : "hidden md:block"
        )}
      >
        {/* Status Update Buttons */}
        {showStatusManagement && handleStatusChange && (
          <ApplicationStatusManagement
            application={application}
            statusUpdating={statusUpdating}
            handleStatusChange={handleStatusChange}
          />
        )}
        {canUploadMockInterview && (
          <div className="mt-4">
            {application?.mock_interview_video ? (
              <>
                <Button
                  className="w-full"
                  variant="destructive"
                  disabled={isDeletingVideo}
                  onClick={() => setIsDeleteDialogOpen(true)}
                >
                  Delete interview video
                </Button>
                <ConfirmationDialog
                  open={isDeleteDialogOpen}
                  onOpenChange={setIsDeleteDialogOpen}
                  title="Delete interview video"
                  description="Are you sure you want to delete the interview video? This action cannot be undone."
                  confirmText="Delete"
                  cancelText="Cancel"
                  onConfirm={handleDeleteVideo}
                  loading={isDeletingVideo}
                  variant="destructive"
                />
              </>
            ) : (
              <>
                <input
                  type="file"
                  accept="video/mp4,video/webm,video/ogg,video/quicktime"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleUploadVideo}
                />
                <Button
                  className="w-full"
                  disabled={isUploadingVideo}
                  onClick={() => {
                    fileInputRef.current?.click();
                  }}
                >
                  Upload interview video
                </Button>
              </>
            )}
          </div>
        )}
        {canViewCalendlyLink && application.calendly_link && (
          <div className={!canUploadMockInterview ? "pt-10" : "pt-4"}>
            <h3 className="text-lg font-semibold mb-4">Super user only</h3>
            <Button
              className="w-full"
              onClick={() => {
                window.open(application.calendly_link, "_blank");
              }}
            >
              Company calendly
            </Button>
          </div>
        )}
        {canEditInterviewDate &&
          application.status === "invited_to_interview" && (
            <div>
              <div className="mt-6 rounded-xl border border-gray-200 bg-gradient-to-b from-white via-white to-gray-50 p-4 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-semibold text-gray-900">
                    Set interview date and time
                  </h4>
                </div>
                <div className="mt-4 space-y-4">
                  <div className="space-y-2">
                    <Popover
                      open={isCalendarOpen}
                      onOpenChange={setIsCalendarOpen}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          {selectedInterviewDate ? (
                            <span>{formattedInterviewDate}</span>
                          ) : (
                            <span className="text-muted-foreground">
                              Pick a date
                            </span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto min-w-[18rem] p-0"
                        align="start"
                      >
                        <Calendar
                          mode="single"
                          selected={selectedInterviewDate ?? undefined}
                          onSelect={(date) => {
                            setSelectedInterviewDate(date ?? null);
                            if (date) {
                              setIsCalendarOpen(false);
                            }
                          }}
                          disabled={(date) =>
                            date < new Date(new Date().setHours(0, 0, 0, 0))
                          }
                          autoFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900">
                      Interview time
                    </h4>
                    <Select
                      value={selectedInterviewTime}
                      onValueChange={setSelectedInterviewTime}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a time" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_OPTIONS.map((timeOption) => (
                          <SelectItem key={timeOption} value={timeOption}>
                            {timeOption}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    className="w-full bg-[#471999] hover:bg-[#471999]/90"
                    onClick={handleSaveInterviewDate}
                    disabled={
                      isSavingInterviewDate ||
                      !selectedInterviewDate ||
                      !selectedInterviewTime
                    }
                  >
                    {isSavingInterviewDate ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save interview slot"
                    )}
                  </Button>
                </div>
              </div>
              <div className="space-y-2 pt-4">
                <h4 className="font-semibold text-gray-900">Interview link</h4>
                <Input
                  value={interviewLinkValue}
                  onChange={(event: any) =>
                    setInterviewLinkValue(event.target.value)
                  }
                  placeholder="https://example.com/interview"
                />
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleInterviewLinkUpdate}
                    disabled={isUpdatingInterviewLink}
                  >
                    {isUpdatingInterviewLink ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Update link"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}
